// import { ArrowRight, Sparkles } from "lucide-react";
// import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import video from "../asset/Images/vi.mp4";
// import videoWebm from "../asset/Images/vi.webm"; // uncomment once you export a WebM version
import videoPoster from "../asset/Images/12services/vi-poster.jpg"; // a lightweight thumbnail frame from the video

const ServicesHero = () => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef(null);
  const sectionRef = useRef(null);

  // Handle video load events
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleLoadedData = () => {
      setIsVideoLoaded(true);
      // Auto-play only after data is loaded
      videoElement.play()
        .then(() => setIsVideoPlaying(true))
        .catch(() => { });
    };

    const handlePlay = () => setIsVideoPlaying(true);
    const handlePause = () => setIsVideoPlaying(false);

    videoElement.addEventListener('loadeddata', handleLoadedData);
    videoElement.addEventListener('play', handlePlay);
    videoElement.addEventListener('pause', handlePause);

    // Force load immediately since this is a hero (above the fold)
    if (videoElement.readyState === 0) {
      videoElement.load();
    }

    return () => {
      videoElement.removeEventListener('loadeddata', handleLoadedData);
      videoElement.removeEventListener('play', handlePlay);
      videoElement.removeEventListener('pause', handlePause);
    };
  }, []);

  // Intersection Observer — pauses video when scrolled away, resumes when back in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && videoRef.current) {
            if (isVideoLoaded) {
              videoRef.current.play().catch(() => { });
            }
          } else if (videoRef.current) {
            videoRef.current.pause();
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [isVideoLoaded]);

  // Visibility change (tab switching)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && videoRef.current) {
        videoRef.current.pause();
      } else if (!document.hidden && isVideoLoaded && videoRef.current) {
        videoRef.current.play().catch(() => { });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isVideoLoaded]);

  return (
    <section
      ref={sectionRef}
      className="relative w-full bg-white py-14 lg:py-20 overflow-hidden min-h-[500px]"
    >
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/3 -translate-x-1/2 -translate-y-1/2
        w-[400px] h-[400px] bg-purple-500/10 blur-[100px]
        rounded-full pointer-events-none"
      />

      <div className="relative z-10 w-full max-w-full mx-auto px-20">
        {/* TWO COLUMNS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 xl:gap-24 items-start lg:items-center">
          {/* LEFT — TEXT */}
          <div className="text-center lg:text-left">
            <h1 className="text-4xl sm:text-5xl lg:text-5xl xl:text-6xl
              font-extrabold text-[#0f0f0f] tracking-tight
              leading-[1.1] mb-5"
            >
              Lets build products for {" "}
              <span className="bg-gradient-to-r from-[#7c3aed] to-[#e83e8c]
                bg-clip-text text-transparent"
              >
                Smarter Life
              </span>
            </h1>

            <p className="max-w-xl mx-auto lg:mx-0 text-base sm:text-lg
              text-gray-600 leading-relaxed mb-7"
            >
              We turn ideas into products that work, perform, and are built to scale.From mechanical and electronics engineering to embedded systems, software, prototyping, and production. 
            </p>


          </div>

          {/* RIGHT — VIDEO */}
          <div className="w-full">
            <div className="relative w-full aspect-video overflow-hidden rounded-2xl
              shadow-xl border border-gray-100 bg-gradient-to-br from-purple-100 to-pink-100"
            >
              {/* ALWAYS VISIBLE GRADIENT BACKGROUND - Never blank */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-50/80 via-purple-100/50 to-pink-100/80 z-0" />

              {/* ANIMATED LOADING INDICATOR — only shows briefly since poster covers most of the wait */}
              {!isVideoLoaded && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center">
                  <div className="relative">
                    {/* Spinner */}
                    <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
                  </div>
                  <p className="mt-4 text-sm text-purple-600 font-medium animate-pulse">
                    Loading video...
                  </p>
                </div>
              )}

              {/* VIDEO ELEMENT — loads immediately since this is above-the-fold hero content.
                  No IntersectionObserver gating on the source here (that pattern is only
                  for videos further down the page, not the hero itself). */}
              <video
                ref={videoRef}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 z-1 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                poster={videoPoster}
                muted
                playsInline
                autoPlay
                loop
                preload="auto"
                disablePictureInPicture
                controlsList="nodownload nofullscreen noremoteplayback"
                onContextMenu={(e) => e.preventDefault()}
                title="Mechatronics Engineering"
              >
                {/* Uncomment the WebM source once you export one with HandBrake —
                    browsers try sources top-to-bottom, so WebM (smaller) goes first */}
                {/* <source src={videoWebm} type="video/webm" /> */}
                <source src={video} type="video/mp4" />
                Your browser doesn't support video playback.
              </video>
              {/* Overlay Gradient */}
              <div className="absolute inset-0 bg-gradient-to-tr
                from-purple-900/10 to-pink-500/10 pointer-events-none z-2"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesHero;